(() => {
  if (window.__seednaryCartDrawerLoaded) return;
  window.__seednaryCartDrawerLoaded = true;

  const rootPath = () => (window.Shopify?.routes?.root || "/").replace(/\/$/, "");
  const cartAddUrl = () => `${rootPath()}/cart/add.js`;
  const cartChangeUrl = () => `${rootPath()}/cart/change.js`;
  const cartUrl = () => `${rootPath()}/cart.js`;

  const drawer = () => document.querySelector("[data-seednary-cart-drawer]");
  let refreshPromise = null;
  let cartMutationQueue = Promise.resolve();
  let externalRefreshTimer = null;

  const setOpen = (open) => {
    const node = drawer();
    if (!node) return false;
    node.classList.toggle("is-open", open);
    node.setAttribute("aria-hidden", open ? "false" : "true");
    document.documentElement.classList.toggle("seednary-cart-open", open);
    return true;
  };

  const setDrawerUpdating = (updating) => {
    const node = drawer();
    if (node) node.classList.toggle("is-updating", updating);
  };

  const updateCartCount = (cartOrCount) => {
    const count = typeof cartOrCount === "number" ? cartOrCount : cartOrCount?.item_count ?? 0;
    document.querySelectorAll("[data-cart-count], .cart-count, .cart-bubble__text-count, .cart-count-bubble span:first-child").forEach((node) => {
      node.textContent = count;
      node.classList.toggle("hidden", count === 0);
    });
  };

  const getCart = () => fetch(cartUrl(), {
    headers: { Accept: "application/json" },
    cache: "no-store",
  }).then((res) => res.json());

  const parseDrawerHtml = (html) => {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.querySelector("[data-seednary-cart-drawer]");
  };

  const patchNode = (currentRoot, freshRoot, selector) => {
    const current = currentRoot.querySelector(selector);
    const fresh = freshRoot.querySelector(selector);
    if (!current || !fresh) return false;
    current.replaceWith(fresh);
    return true;
  };

  const patchDrawer = (fresh) => {
    const current = drawer();
    if (!current || !fresh) return false;

    const wasOpen = current.classList.contains("is-open");
    let patched = false;

    const currentCount = current.querySelector(".seednary-cart-drawer__top [data-cart-count]");
    const freshCount = fresh.querySelector(".seednary-cart-drawer__top [data-cart-count]");
    if (currentCount && freshCount) {
      currentCount.textContent = freshCount.textContent;
      const countValue = Number(freshCount.textContent || 0);
      if (!Number.isNaN(countValue)) updateCartCount(countValue);
      patched = true;
    }

    patched = patchNode(current, fresh, "[data-seednary-cart-items]") || patched;
    patched = patchNode(current, fresh, ".seednary-cart-drawer__footer") || patched;

    if (!patched) {
      current.replaceWith(fresh);
    }

    if (wasOpen) setOpen(true);
    return true;
  };

  const patchDrawerFromSections = (sections) => {
    const node = drawer();
    const sectionId = node?.dataset.seednaryCartSectionId;
    const html = sectionId ? sections?.[sectionId] : null;
    const fresh = parseDrawerHtml(html);
    if (!fresh) return false;
    return patchDrawer(fresh);
  };

  const sectionRequestUrl = () => {
    const node = drawer();
    const sectionId = node?.dataset.seednaryCartSectionId;
    if (!sectionId) return null;
    const url = new URL(window.location.href);
    url.searchParams.set("sections", sectionId);
    return `${url.pathname}${url.search}`;
  };

  const refreshDrawer = async () => {
    const requestUrl = sectionRequestUrl();
    if (!requestUrl) return false;
    if (refreshPromise) return refreshPromise;

    refreshPromise = fetch(requestUrl, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    })
      .then((response) => response.json())
      .then((sections) => patchDrawerFromSections(sections))
      .catch((error) => {
        console.error("[seednary cart] Refresh failed", error);
        return false;
      })
      .finally(() => {
        refreshPromise = null;
      });

    return refreshPromise;
  };

  const scheduleExternalRefresh = (delay = 180) => {
    window.clearTimeout(externalRefreshTimer);
    externalRefreshTimer = window.setTimeout(() => {
      refreshDrawer().catch(() => {});
    }, delay);
  };

  const setButtonLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle("is-loading", loading);
    button.toggleAttribute("aria-busy", loading);
    button.disabled = loading;
  };

  const enqueueCartMutation = (task) => {
    const next = cartMutationQueue.catch(() => {}).then(task);
    cartMutationQueue = next;
    return next;
  };

  const addForm = async (form) => {
    const submit = form.querySelector("[data-seednary-cart-recommendation-add]") || form.querySelector("button");
    if (submit?.disabled) return;

    setButtonLoading(submit, true);
    setDrawerUpdating(true);

    try {
      const formData = new FormData(form);
      const response = await fetch(cartAddUrl(), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      });
      const added = await response.json().catch(() => ({}));
      if (!response.ok || added.status) throw new Error(added.description || added.message || "Add to cart failed");

      setOpen(true);
      submit.dataset.originalText = submit.dataset.originalText || submit.textContent;
      submit.textContent = "Added";

      getCart().then(updateCartCount).catch(() => {});
      await refreshDrawer();
    } catch (error) {
      console.error("[seednary cart] Add failed", error);
      document.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { data: error } }));
    } finally {
      window.setTimeout(() => {
        if (submit?.dataset?.originalText) submit.textContent = submit.dataset.originalText;
        setButtonLoading(submit, false);
      }, 350);
      setDrawerUpdating(false);
    }
  };

  const changeLine = async (key, quantity, trigger) => {
    if (!key || trigger?.disabled) return;

    setButtonLoading(trigger, true);
    setDrawerUpdating(true);

    const sectionId = drawer()?.dataset.seednaryCartSectionId;
    const payload = {
      id: key,
      quantity: Math.max(0, Number(quantity) || 0),
      sections: sectionId ? [sectionId] : [],
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(cartChangeUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });

      const cart = await response.json().catch(() => ({}));
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || "Cart update failed");

      updateCartCount(cart);
      if (!patchDrawerFromSections(cart.sections)) {
        await refreshDrawer();
      }
      setOpen(true);
    } catch (error) {
      console.error("[seednary cart] Change failed", error);
      await refreshDrawer().catch(() => {});
      setOpen(true);
      document.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { data: error } }));
    } finally {
      setButtonLoading(trigger, false);
      setDrawerUpdating(false);
    }
  };

  const syncDrawerFromCartEvent = (event) => {
    if (event?.detail?.source === "seednary-cart-drawer") return;
    if (event?.detail?.seednaryCartDrawer === true) return;

    const sections = event?.detail?.data?.sections || event?.detail?.sections || {};
    if (patchDrawerFromSections(sections)) return;
    scheduleExternalRefresh();
  };

  document.addEventListener("click", (event) => {
    const open = event.target.closest?.("[data-seednary-cart-open]");
    if (open) {
      event.preventDefault();
      setOpen(true);
      return;
    }

    if (event.target.closest?.("[data-seednary-cart-close]")) {
      event.preventDefault();
      setOpen(false);
      return;
    }

    const add = event.target.closest?.("[data-seednary-cart-recommendation-add]");
    if (add) {
      event.preventDefault();
      const form = add.closest("[data-seednary-cart-recommendation-form]");
      if (form) enqueueCartMutation(() => addForm(form));
      return;
    }

    const change = event.target.closest?.("[data-seednary-cart-change]");
    if (change) {
      event.preventDefault();
      enqueueCartMutation(() => changeLine(change.dataset.seednaryCartChange, change.dataset.quantity, change));
      return;
    }

    const remove = event.target.closest?.("[data-seednary-cart-remove]");
    if (remove) {
      event.preventDefault();
      enqueueCartMutation(() => changeLine(remove.dataset.seednaryCartRemove, 0, remove));
    }
  });

  document.addEventListener("submit", async (event) => {
    const discountForm = event.target.closest?.("[data-seednary-discount-form]");
    if (!discountForm) return;
    event.preventDefault();
    const code = new FormData(discountForm).get("discount")?.toString().trim();
    if (!code) return;
    setDrawerUpdating(true);
    await fetch(`${rootPath()}/discount/${encodeURIComponent(code)}?redirect=/cart`, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    }).catch(() => {});
    await refreshDrawer();
    setDrawerUpdating(false);
  });

  document.addEventListener("seednary:cart:open", () => {
    setOpen(true);
    scheduleExternalRefresh(80);
  });

  document.addEventListener("cart:update", syncDrawerFromCartEvent);
  document.addEventListener("cart:updated", syncDrawerFromCartEvent);
  document.addEventListener("cart:refresh", syncDrawerFromCartEvent);
})();
